import { createIcons, Menu, X, Sun, Moon, Search, Bookmark, ArrowUpRight, ArrowDownRight, ArrowRight, ArrowLeft, Zap, Circle, Link, Share2, Wallet, Gauge, Headphones, Globe, LayoutDashboard, ScanEye, LogOut, Layers3, Plus, GitBranch, Database, FileText, Files, SlidersHorizontal, ShieldCheck, History, Eye, Archive, Save, Send, Trash2, CircleCheck, CircleAlert } from "lucide";
import { initAdmin } from "./admin";
import { initObservation } from "./observation";
import { initSearch } from "./search";
import { initReader } from "./reader";
import { initEffects } from "./effects";
import { notify } from "./feedback";

initObservation();
initSearch();
initReader();
initEffects();

const iconSet = { Menu, X, Sun, Moon, Search, Bookmark, ArrowUpRight, ArrowDownRight, ArrowRight, ArrowLeft, Zap, Circle, Link, Share2, Wallet, Gauge, Headphones, Globe, LayoutDashboard, ScanEye, LogOut, Layers3, Plus, GitBranch, Database, FileText, Files, SlidersHorizontal, ShieldCheck, History, Eye, Archive, Save, Send, Trash2, CircleCheck, CircleAlert };
function renderIcons(): void {
  createIcons({ icons: iconSet, attrs: { "aria-hidden": "true" } });
}
const root = document.documentElement;
const themeToggle = document.querySelector<HTMLButtonElement>("[data-theme-toggle]");

function setTheme(theme: "light" | "dark"): void {
  root.dataset.theme = theme;
  const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (themeColor) themeColor.content = getComputedStyle(root).getPropertyValue("--paper").trim();
  localStorage.setItem("vpswatcher-theme", theme);
  if (themeToggle) {
    themeToggle.innerHTML = `<i data-lucide="${theme === "dark" ? "moon" : "sun"}"></i>`;
    themeToggle.setAttribute("aria-label", theme === "dark" ? "切换到浅色主题" : "切换到深色主题");
  }
  renderIcons();
}

setTheme((localStorage.getItem("vpswatcher-theme") as "light" | "dark" | null) ?? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"));
initAdmin();
themeToggle?.addEventListener("click", () => setTheme(root.dataset.theme === "dark" ? "light" : "dark"));

const menuToggle = document.querySelector<HTMLButtonElement>("[data-menu-toggle]");
const navigation = document.querySelector<HTMLElement>("#primary-navigation");
function setMenu(open: boolean): void {
  if (!menuToggle || menuToggle.getAttribute("aria-expanded") === String(open)) return;
  navigation?.classList.toggle("is-open", open);
  menuToggle?.setAttribute("aria-expanded", String(open));
  menuToggle?.setAttribute("aria-label", open ? "关闭菜单" : "打开菜单");
  if (menuToggle) menuToggle.innerHTML = `<i data-lucide="${open ? "x" : "menu"}"></i>`;
  renderIcons();
}
menuToggle?.addEventListener("click", () => setMenu(menuToggle.getAttribute("aria-expanded") !== "true"));
document.addEventListener("keydown", (event) => {
  if (event.key === "Escape" && menuToggle?.getAttribute("aria-expanded") === "true") {
    setMenu(false);
    menuToggle.focus();
  }
});
document.addEventListener("click", (event) => {
  // Icon replacement detaches the original SVG target; the event path still
  // identifies the button that was clicked.
  const path = event.composedPath();
  if ((!navigation || !path.includes(navigation)) && (!menuToggle || !path.includes(menuToggle))) setMenu(false);
});
navigation?.addEventListener("click", (event) => {
  if (event.target instanceof Element && event.target.closest("a")) setMenu(false);
});
window.matchMedia("(min-width: 1001px)").addEventListener("change", () => setMenu(false));
navigation?.querySelectorAll<HTMLAnchorElement>("a").forEach((link) => {
  if (link.pathname === location.pathname) link.setAttribute("aria-current", "page");
});

document.querySelectorAll<HTMLButtonElement>("[data-save]").forEach((button) => {
  const slug = button.dataset.save;
  if (!slug) return;
  const key = `vpswatcher-saved:${slug}`;
  const update = (saved: boolean) => {
    button.innerHTML = `<i data-lucide="bookmark"></i><span>${saved ? "已收藏" : "收藏"}</span>`;
    button.setAttribute("aria-pressed", String(saved));
    button.setAttribute("aria-label", saved ? "取消收藏文章" : "收藏文章");
    renderIcons();
  };
  update(localStorage.getItem(key) === "true");
  button.addEventListener("click", () => {
    const saved = localStorage.getItem(key) !== "true";
    localStorage.setItem(key, String(saved));
    update(saved);
    notify(saved ? "已保存到本地收藏" : "已取消收藏");
  });
});

document.querySelector<HTMLSelectElement>("[data-sort-select]")?.addEventListener("change", (event) => {
  const select = event.currentTarget as HTMLSelectElement;
  const params = new URLSearchParams(window.location.search);
  params.set("sort", select.value);
  params.delete("page");
  window.location.search = params.toString();
});

document.querySelectorAll<HTMLFormElement>("[data-newsletter-form]").forEach((form) => {
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    const note = form.querySelector<HTMLElement>("[data-form-note]");
    const body = JSON.stringify({ email: new FormData(form).get("email") });
    if (note) note.textContent = "正在保存…";
    try {
      const csrf = document.cookie.split(";").map((part) => part.trim()).find((part) => part.startsWith("vpswatcher_csrf="))?.split("=")[1] ?? "";
      const response = await fetch("/api/v1/newsletter/subscriptions", { method: "POST", headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf }, body });
      const data = await response.json() as { message?: string; error?: { message: string } };
      if (!response.ok) throw new Error(data.error?.message ?? "邮箱登记失败");
      if (note) note.textContent = data.message ?? "邮箱已登记，状态为未验证。";
      form.reset();
    } catch (error) {
      if (note) note.textContent = error instanceof Error ? error.message : "邮箱登记暂时不可用。";
    }
  });
});

if (location.hash === "#scores") {
  const scores = document.querySelector<HTMLDetailsElement>("#scores");
  if (scores) scores.open = true;
}
